import React, { useCallback, useEffect, useState } from 'react';
import * as yup from 'yup';

import SidePanelContentSection from '../../../common2/side-panel/SidePanelContentSection';
import Column from '../../../common/layout/Column';
import Uploader, { UploaderFile } from '../../../common/Uploader';
import InputHelpLink from '../InputHelpLink';
import { ClaimContext, ClaimType, OnChangeClaimContext } from '../types';
import { ClaimStepContainerProps } from './ClaimStepContainer';
import LoadingStep from './LoadingStep';
import { ImportFileState } from './useFileImport';
import OrderItemsInput from './OrderItemsInput';
import OrderItemsCurrencyInput from './OrderItemsCurrencyInput';
import ColumnsLayout from '../../../common/layout/ColumnsLayout';
import Select from '../../../common2/Select';
import useOnFormChange from '../../../../hooks/useOnFormChange';
import OrderItemsTaxInputs from './OrderItemsTaxInputs';
import { useCalculateNetValueBasedOnOrderItems } from './hooks/useCalculateNetValueBasedOnOrderItems';
import { useValidateStep } from './hooks/useValidateStep';
import InvoiceStepBannerMessages from './InvoiceStepBannerMessages';
import CustomerCommentInput from './CustomerCommentInput';
import { Input } from '../../../common2/Input/Input';
import { getCurrencyOfCountry, isInvoiceRequired, isSpecialInvestigationFlow } from '../claimsHelpers';
import './yupClaimsExtensions';
import { useNestedArrayError } from '../../../../hooks/useNestedArrayError';
import { useClaimsShopSettings } from '../../ClaimsShopSettingsContext';

type TaxType = 'exclusive' | 'inclusive-claim' | 'inclusive-order-item';

export type ClaimContextForInvoiceStep = Pick<
  ClaimContext,
  | 'invoiceDocuments'
  | 'outsideOfDeadlineReason'
  | 'type'
  | 'carrierCode'
  | 'carrierCountryCode'
  | 'submissionInfo'
  | 'shipmentId'
  | 'entryPoint'
  | 'netValue'
  | 'netValueCurrency'
  | 'orderItems'
  | 'isReturnShipment'
  | 'orderId'
  | 'isMultiShipmentOrder'
  | 'isOrderItemsRequired'
  | 'cashOnDeliveryPaymentType'
  | 'recipientCountryCode'
  | 'isTaxIncluded'
  | 'taxRate'
  | 'shopClaimId'
  | 'otherDocuments'
  | 'isOrderItemTaxRate'
>;

export type InvoiceStepProps = {
  claim: ClaimContextForInvoiceStep;
  as: React.ComponentType<ClaimStepContainerProps>;
  onChange: OnChangeClaimContext;
  importInvoiceState: ImportFileState;
} & ClaimStepContainerProps;

const schema = yup.object({
  invoiceDocuments: yup
    .array()
    .max(1)
    .when(['type', '$isSpecialInvestigationFlow'], {
      is: (type: ClaimType, isSpecialInvestigationFlow: boolean) =>
        isInvoiceRequired({ type }) && !isSpecialInvestigationFlow,
      then: (schema) => schema.draftableMinOneAsRequired(),
      otherwise: (schema) => schema.notRequired(),
    })
    .label('Invoice document'),
  otherDocuments: yup.array().notRequired().label('Additional documents'),
  orderItems: yup.mixed().when('isOrderItemsRequired', {
    is: true,
    then: () =>
      yup
        .array(
          yup.object().shape({
            unitPrice: yup.number().empty('').draftableRequired().min(0).label('Unit price'),
            quantity: yup.number().empty('').draftableRequired().draftablePositive().label('Quantity').integer(),
            description: yup.string().required().label('Description').max(255),
            taxRate: yup.mixed().when('$taxType', {
              is: (taxType: TaxType) => taxType === 'inclusive-order-item',
              then: () => yup.number().empty('').max(100).min(0).draftableRequired().label('Tax Rate'),
              otherwise: () => yup.mixed().nullable(),
            }),
          })
        )
        .draftableMinOneAsRequired()
        .label('Order items'),
    otherwise: () => yup.mixed().notRequired(),
  }),
  netValue: yup
    .number()
    .empty('')
    .when(['isOrderItemsRequired', '$isSpecialInvestigationFlow'], {
      is: (isOrderItemsRequired: boolean, isSpecialInvestigationFlow: boolean) =>
        isSpecialInvestigationFlow && !isOrderItemsRequired,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) => schema.draftableRequired(),
    })
    .draftablePositive()
    .when('$isNetValueGrossAmount', {
      is: true,
      then: (schema) => schema.label('Gross amount'),
      otherwise: (schema) => schema.label('Net value'),
    }),
  netValueCurrency: yup.string().required().label('Net value currency'),
  cashOnDeliveryPaymentType: yup
    .string()
    .oneOf(['full', 'partial'])
    .label('Cash on delivery payment type')
    .when('type', {
      is: (type: ClaimType) => type === ClaimType.CASH_ON_DELIVERY,
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.nullable(),
    }),
  isTaxIncluded: yup.boolean().label('Tax Type'),
  taxRate: yup
    .number()
    .empty('')
    .draftablePositive()
    .max(100)
    .label('Tax Rate')
    .when('$taxType', {
      is: (taxType: TaxType) => taxType === 'inclusive-claim',
      then: (schema) => schema.draftableRequired(),
      otherwise: (schema) => schema.nullable(),
    }),
});

const cashOnDeliveryPaymentTypeOptions = [
  { value: 'full', label: 'CASH_ON_DELIVERY_PAYMENT_TYPE_FULL' },
  { value: 'partial', label: 'CASH_ON_DELIVERY_PAYMENT_TYPE_PARTIAL' },
];

function InvoiceStep(props: InvoiceStepProps) {
  const {
    claim,
    onChange,
    onNext,
    as: Container,
    importInvoiceState,
    isExtractingInvoice,
    isFailedToExtractInvoice,
    extractInvoiceData,
    ...restProps
  } = props;
  const { isOrderItemsRequired, cashOnDeliveryPaymentType, isTaxIncluded, taxRate, shopClaimId, isOrderItemTaxRate } =
    claim;

  const shouldDisplayOrderItemTaxRate = !!isOrderItemTaxRate && !!isTaxIncluded;

  const isCashOnDeliveryClaim = claim.type === ClaimType.CASH_ON_DELIVERY;

  const shopSettings = useClaimsShopSettings();
  const isSpecialFlow = isSpecialInvestigationFlow({ shopSettings, type: claim.type });

  const onInputChange = useOnFormChange(onChange);
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);

  let taxType = '';
  if (!isOrderItemsRequired || !isTaxIncluded) {
    taxType = 'exclusive';
  } else if (!isOrderItemTaxRate) {
    taxType = 'inclusive-claim';
  } else {
    taxType = 'inclusive-order-item';
  }

  const { onClickNext, onClickSaveDraft, onClickBack, errors } = useValidateStep(
    schema,
    claim,
    {
      isNetValueGrossAmount: isCashOnDeliveryClaim,
      // we put it into context so it can be accessed in nested order item validation
      taxType,
      isSpecialInvestigationFlow: isSpecialFlow,
    },
    props
  );
  const otherDocumentsError = useNestedArrayError('otherDocuments', errors, claim.otherDocuments);

  useCalculateNetValueBasedOnOrderItems(!!isOrderItemsRequired, onChange, claim);

  // sets currency on mount based on claim country
  useEffect(() => {
    if (!claim.netValueCurrency) {
      onChange({
        netValueCurrency: getCurrencyOfCountry(claim.carrierCountryCode || claim.recipientCountryCode),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInvoiceInputChange = useCallback(
    (value: UploaderFile[], name: string) => {
      if (value.length > 0) {
        const file = value[0];
        const fileId = Number(file?.response?.body?.id);
        extractInvoiceData && extractInvoiceData(fileId);
      }

      return onChange({ [name]: value });
    },
    [extractInvoiceData, onChange]
  );

  if (importInvoiceState === 'searching') {
    return <LoadingStep {...props} text="CLAIMS_IMPORT_INVOICE_SEARCHING" />;
  }

  return (
    <Container
      {...restProps}
      onSaveDraft={onClickSaveDraft}
      onNext={onClickNext}
      onBack={onClickBack}
      isDisabled={isNextButtonDisabled || isExtractingInvoice}
    >
      <InvoiceStepBannerMessages
        claim={claim}
        isFailedToExtractInvoice={isFailedToExtractInvoice}
        importInvoiceState={importInvoiceState}
      />
      <Column>
        <SidePanelContentSection heading="CLAIMS_DETAILS_SECTION_INVOICE_HEADER">
          <Uploader
            name="invoiceDocuments"
            files={claim.invoiceDocuments}
            label="CLAIMS_DOCUMENTS_UPLOAD_INVOICE_DOCUMENT"
            topRightElement={
              // eslint-disable-next-line max-len
              <InputHelpLink to="https://support.portal.sevensenders.com/support/solutions/articles/15000048062-submission-document-requirements#Invoice-Requirements%C2%A0">
                Invoice requirements
              </InputHelpLink>
            }
            isRequired={isInvoiceRequired(claim) && !isSpecialFlow}
            errorWithTranslation={errors.invoiceDocuments}
            allowedFileTypes={['application/pdf', 'image/jpg', 'image/jpeg', 'image/png']}
            helpText="CLAIMS_DOCUMENTS_UPLOAD_INVOICE_DOCUMENT_HELP"
            onChange={onInvoiceInputChange}
            onBusy={setIsNextButtonDisabled}
            maxNumberOfFiles={1}
            isExternallyLoading={isExtractingInvoice}
            externalLoadingText="UPLOADER_ANALYSIS_LOADING_TEXT"
          />
        </SidePanelContentSection>
        <SidePanelContentSection heading="CLAIMS_DETAILS_SECTION_NET_VALUE_HEADER">
          <Column spacing="small">
            {isOrderItemsRequired && (
              <OrderItemsInput
                orderItems={claim.orderItems}
                errors={errors}
                onChange={onInputChange}
                isDisabled={isExtractingInvoice}
                isTaxRateShown={shouldDisplayOrderItemTaxRate}
              />
            )}
            {isOrderItemsRequired && (
              <OrderItemsTaxInputs
                taxRate={taxRate}
                isTaxIncluded={isTaxIncluded}
                onChange={onChange}
                errors={errors}
                isDisabled={isExtractingInvoice}
                isOrderItemTaxRate={isOrderItemTaxRate}
              />
            )}
            {!isCashOnDeliveryClaim ? (
              <OrderItemsCurrencyInput
                amount={claim.netValue}
                currency={claim.netValueCurrency}
                amountName="netValue"
                currencyName="netValueCurrency"
                label="CLAIM_TOTAL_VALUE"
                onChange={onChange}
                error={errors.netValue}
                isRequired={!isSpecialFlow || !!isOrderItemsRequired}
                noErrorTranslation
                readonlyNetValue={isOrderItemsRequired}
                isDisabled={isExtractingInvoice}
              />
            ) : (
              <ColumnsLayout>
                <OrderItemsCurrencyInput
                  amount={claim.netValue}
                  currency={claim.netValueCurrency}
                  amountName="netValue"
                  currencyName="netValueCurrency"
                  label="CLAIM_GROSS_AMOUNT"
                  onChange={onChange}
                  error={errors.netValue}
                  isRequired
                  noErrorTranslation
                  readonlyNetValue={isOrderItemsRequired}
                  isDisabled={isExtractingInvoice}
                />
                <Select
                  label="Payment type"
                  placeholder="Select payment type"
                  name="cashOnDeliveryPaymentType"
                  value={cashOnDeliveryPaymentType}
                  options={cashOnDeliveryPaymentTypeOptions}
                  onChange={onInputChange}
                  error={errors.cashOnDeliveryPaymentType}
                  isRequired
                />
              </ColumnsLayout>
            )}
          </Column>
        </SidePanelContentSection>
        <SidePanelContentSection heading="CLAIMS_DETAILS_SECTION_ADDITIONAL_DETAILS">
          <Column spacing="small">
            <CustomerCommentInput claim={claim} onChange={onInputChange} />
            <Uploader
              name="otherDocuments"
              errorWithTranslation={otherDocumentsError}
              maxNumberOfFiles={10}
              label="Documents"
              onChange={onInputChange}
              files={claim.otherDocuments}
              allowedFileTypes={['application/pdf', 'image/jpg', 'image/jpeg', 'image/png']}
            />
            <ColumnsLayout>
              <Input
                name="shopClaimId"
                label="Internal claim ID"
                placeholder="Add internal claim ID"
                value={shopClaimId}
                error={errors.shopClaimId}
                onChange={onInputChange}
                noErrorTranslation
              />
            </ColumnsLayout>
          </Column>
        </SidePanelContentSection>
      </Column>
    </Container>
  );
}

export default React.memo(InvoiceStep);
